/**
 * Worker coordination logic (RFC 0215: Log substrate).
 *
 * Pure decision logic over the DAG. The workers that call this live in RFC 0216;
 * the logic lives here so both peers (mod-core, Searugast) compute the same
 * answers and the conformance vectors can pin them.
 */

import { ancestors } from './dag'
import type { Entry, EntryId, LogDoc, ToolWorkerId } from './types'

/**
 * The selected tool worker at a point in the DAG — mod's `selected_tool_worker`
 * (whiffletree parity).
 *
 * Purely worker_migration-derived: walk back to the nearest `worker_migration`
 * ancestor (greatest createdAt; ties broken lexicographically on `workerId`, per
 * Searugast) and take its `workerId`. No migration means no selection (`null`) —
 * an open claim, the single-device default.
 *
 * There is deliberately no fall back to a per-run device field: whiffletree's
 * `RunEvent` has none, and mod's `initialToolWorkerId` was retired (RFC 0261
 * slice 3). Placement flows through `worker_migration` alone, the way searugast
 * models it.
 */
export function activeToolWorker(
  doc: LogDoc,
  at: EntryId,
): ToolWorkerId | null {
  const lineage: Entry[] = [doc.entries[at], ...ancestors(doc, at)].filter(
    (e): e is Entry => !!e,
  )

  const migrations = lineage.filter(
    (e) => e.payload.type === 'worker_migration',
  )
  if (migrations.length === 0) return null

  const nearest = migrations.reduce((best, m) => {
    const bm = best.payload as {
      type: 'worker_migration'
      payload: { workerId: string }
    }
    const mm = m.payload as {
      type: 'worker_migration'
      payload: { workerId: string }
    }
    if (m.createdAt > best.createdAt) return m
    if (
      m.createdAt === best.createdAt &&
      mm.payload.workerId < bm.payload.workerId
    ) {
      return m
    }
    return best
  })
  return nearest.payload.type === 'worker_migration'
    ? nearest.payload.payload.workerId
    : null
}

/**
 * Resolve a fork between concurrent assistant messages.
 *
 * A message wins its fork once a tool_result has been published against one of
 * its tool_use blocks (the pinning event). Before any tool_result exists the
 * fork is open and the caller should fall back to LWW (latest createdAt).
 * Returns the winning message id, or null if unresolved.
 */
export function inferenceForkWinner(
  doc: LogDoc,
  messageIds: EntryId[],
): EntryId | null {
  for (const id of messageIds) {
    const msg = doc.entries[id]
    if (msg?.payload.type !== 'message') continue
    const blockIds = new Set(msg.payload.payload.toolUses.map((b) => b.id))
    const pinned = Object.values(doc.entries).some(
      (e) =>
        e.payload.type === 'message' &&
        e.payload.payload.role === 'tool_result' &&
        e.payload.payload.toolUseId != null &&
        blockIds.has(e.payload.payload.toolUseId),
    )
    if (pinned) return id
  }
  return null
}
